import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Calculator, Target, DollarSign, TrendingUp, Download, ArrowLeft, FileText, BarChart3, Users, Phone, FileCheck, ClipboardList, CheckCircle, HandCoins } from 'lucide-react';

const BlueprintBreakDown = () => {
  const [inputs, setInputs] = useState({
    settlementGoal: '35000000', // $35M default
    averageLoanAmount: '485000', // $485K default
    workingDaysPerMonth: '20',
    brokerSplit: '50', // 50% default
    // Conversion rates (default 70%)
    leadToDiscovery: '70',
    discoveryToPortal: '70',
    portalToFactFind: '70',
    factFindToAssessment: '70',
    assessmentToProposal: '70',
    proposalToApplication: '70',
    applicationToLodgement: '70',
    lodgementToApproval: '70',
    approvalToSettlement: '70'
  });

  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const handleInputChange = (field, value) => {
    setInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-AU').format(Math.ceil(num || 0));
  };

  const calculateBlueprint = () => {
    const settlementGoal = parseFloat(inputs.settlementGoal) || 0;
    const averageLoanAmount = parseFloat(inputs.averageLoanAmount) || 485000;
    const workingDays = parseInt(inputs.workingDaysPerMonth) || 20;
    const brokerSplit = parseFloat(inputs.brokerSplit) / 100 || 0.5;

    // Convert percentage inputs to decimals
    const conversionRates = {
      leadToDiscovery: parseFloat(inputs.leadToDiscovery) / 100,
      discoveryToPortal: parseFloat(inputs.discoveryToPortal) / 100,
      portalToFactFind: parseFloat(inputs.portalToFactFind) / 100,
      factFindToAssessment: parseFloat(inputs.factFindToAssessment) / 100,
      assessmentToProposal: parseFloat(inputs.assessmentToProposal) / 100,
      proposalToApplication: parseFloat(inputs.proposalToApplication) / 100,
      applicationToLodgement: parseFloat(inputs.applicationToLodgement) / 100,
      lodgementToApproval: parseFloat(inputs.lodgementToApproval) / 100,
      approvalToSettlement: parseFloat(inputs.approvalToSettlement) / 100
    };

    // Calculate required settlements
    const settlements = Math.ceil(settlementGoal / averageLoanAmount);

    // Work backwards through the funnel
    const approvals = Math.ceil(settlements / conversionRates.approvalToSettlement);
    const lodgements = Math.ceil(approvals / conversionRates.lodgementToApproval);
    const applications = Math.ceil(lodgements / conversionRates.applicationToLodgement);
    const proposals = Math.ceil(applications / conversionRates.proposalToApplication);
    const assessments = Math.ceil(proposals / conversionRates.assessmentToProposal);
    const factFinds = Math.ceil(assessments / conversionRates.factFindToAssessment);
    const portalAdds = Math.ceil(factFinds / conversionRates.portalToFactFind);
    const discoveryC = Math.ceil(portalAdds / conversionRates.discoveryToPortal);
    const leads = Math.ceil(discoveryC / conversionRates.leadToDiscovery);

    // Calculate daily and weekly volumes
    const calculatePeriods = (monthlyVolume) => ({
      monthly: monthlyVolume,
      weekly: Math.ceil(monthlyVolume / 4.33), // Average weeks per month
      daily: Math.ceil(monthlyVolume / workingDays)
    });

    // Commission calculations
    const grossCommission = settlementGoal * 0.0066; // 0.66%
    const brokerCommission = grossCommission * brokerSplit;
    const companyCommission = grossCommission - brokerCommission;

    const calculatedResults = {
      // Input summary
      settlementGoal,
      averageLoanAmount,
      workingDays,
      brokerSplit: brokerSplit * 100,
      
      // Funnel stages with periods
      funnel: {
        settlements: calculatePeriods(settlements),
        approvals: calculatePeriods(approvals),
        lodgements: calculatePeriods(lodgements),
        applications: calculatePeriods(applications),
        proposals: calculatePeriods(proposals),
        assessments: calculatePeriods(assessments),
        factFinds: calculatePeriods(factFinds),
        portalAdds: calculatePeriods(portalAdds),
        discovery: calculatePeriods(discoveryC),
        leads: calculatePeriods(leads)
      },
      
      // Commission breakdown
      commission: {
        gross: grossCommission,
        broker: brokerCommission,
        company: companyCommission
      },

      // Conversion rates for display
      conversionRates
    };

    setResults(calculatedResults);
    setShowResults(true);
  };

  const resetCalculator = () => {
    setInputs({
      settlementGoal: '35000000',
      averageLoanAmount: '485000',
      workingDaysPerMonth: '20',
      brokerSplit: '50',
      leadToDiscovery: '70',
      discoveryToPortal: '70',
      portalToFactFind: '70',
      factFindToAssessment: '70',
      assessmentToProposal: '70',
      proposalToApplication: '70',
      applicationToLodgement: '70',
      lodgementToApproval: '70',
      approvalToSettlement: '70'
    });
    setResults(null);
    setShowResults(false);
  };

  const exportToPDF = () => {
    window.print();
  };

  const exportToCSV = () => {
    if (!results) return;

    const csvData = [
      ['Blueprint Break Down Report'],
      ['Generated:', new Date().toLocaleDateString('en-AU')],
      [''],
      ['Input Summary'],
      ['Settlement Goal', formatCurrency(results.settlementGoal)],
      ['Average Loan Amount', formatCurrency(results.averageLoanAmount)],
      ['Working Days per Month', results.workingDays],
      ['Broker Commission Split', `${results.brokerSplit}%`],
      [''],
      ['Required Activity Levels'],
      ['Stage', 'Monthly', 'Weekly', 'Daily'],
      ['Leads Required', results.funnel.leads.monthly, results.funnel.leads.weekly, results.funnel.leads.daily],
      ['Discovery Calls', results.funnel.discovery.monthly, results.funnel.discovery.weekly, results.funnel.discovery.daily],
      ['Portal Adds', results.funnel.portalAdds.monthly, results.funnel.portalAdds.weekly, results.funnel.portalAdds.daily],
      ['Fact Find + Docs', results.funnel.factFinds.monthly, results.funnel.factFinds.weekly, results.funnel.factFinds.daily],
      ['Loan Assessments', results.funnel.assessments.monthly, results.funnel.assessments.weekly, results.funnel.assessments.daily],
      ['Proposal Calls', results.funnel.proposals.monthly, results.funnel.proposals.weekly, results.funnel.proposals.daily],
      ['Applications', results.funnel.applications.monthly, results.funnel.applications.weekly, results.funnel.applications.daily],
      ['Lodgements', results.funnel.lodgements.monthly, results.funnel.lodgements.weekly, results.funnel.lodgements.daily],
      ['Approvals', results.funnel.approvals.monthly, results.funnel.approvals.weekly, results.funnel.approvals.daily],
      ['Settlements', results.funnel.settlements.monthly, results.funnel.settlements.weekly, results.funnel.settlements.daily],
      [''],
      ['Commission Breakdown'],
      ['Gross Commission (0.66%)', formatCurrency(results.commission.gross)],
      ['Broker Commission', formatCurrency(results.commission.broker)],
      ['Company Commission', formatCurrency(results.commission.company)],
      [''],
      ['Disclaimer: This calculator provides estimates for planning purposes only and should not be considered as guaranteed outcomes.']
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'blueprint-breakdown-report.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const funnelStages = [
    { key: 'leads', label: 'Lead Generation', icon: Users, color: 'bg-red-50 border-red-200 text-red-700' },
    { key: 'discovery', label: 'Discovery Calls', icon: Phone, color: 'bg-orange-50 border-orange-200 text-orange-700' },
    { key: 'portalAdds', label: 'Fact Find Portal Adds', icon: FileText, color: 'bg-yellow-50 border-yellow-200 text-yellow-700' },
    { key: 'factFinds', label: 'Fact Find + Docs Completed', icon: FileCheck, color: 'bg-lime-50 border-lime-200 text-lime-700' },
    { key: 'assessments', label: 'Loan Assessment', icon: BarChart3, color: 'bg-green-50 border-green-200 text-green-700' },
    { key: 'proposals', label: 'Loan Proposal Calls', icon: Phone, color: 'bg-teal-50 border-teal-200 text-teal-700' },
    { key: 'applications', label: 'Application Preparation', icon: ClipboardList, color: 'bg-cyan-50 border-cyan-200 text-cyan-700' },
    { key: 'lodgements', label: 'Loan Lodgement', icon: FileText, color: 'bg-blue-50 border-blue-200 text-blue-700' },
    { key: 'approvals', label: 'Loan Approval', icon: CheckCircle, color: 'bg-indigo-50 border-indigo-200 text-indigo-700' },
    { key: 'settlements', label: 'Loan Settlement', icon: HandCoins, color: 'bg-purple-50 border-purple-200 text-purple-700' }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-page">
      <Header />
      
      <div className="flex-1 py-8">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Button 
                variant="ghost" 
                onClick={() => window.history.back()}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Target className="h-8 w-8 text-purple-600" />
                </div>
                <h1 className="text-4xl font-bold text-gray-900">
                  Blueprint Break Down
                </h1>
              </div>
              <p className="text-lg text-gray-600 max-w-4xl mx-auto">
                Calculate exactly how much activity you need to achieve your monthly settlement goals 
                and discover your potential commission earnings at every stage of the sales funnel.
              </p>
            </div>
          </div>

          {!showResults ? (
            /* Input Form */
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Goals & Basics */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-purple-600" />
                    Goals & Basics
                  </CardTitle>
                  <CardDescription>
                    Set your monthly targets and basic parameters
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Monthly Settlement Goal ($)
                    </label>
                    <Input
                      type="number"
                      placeholder="35000000"
                      value={inputs.settlementGoal}
                      onChange={(e) => handleInputChange('settlementGoal', e.target.value)}
                      className="text-lg"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Total dollar amount you want to settle per month
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Average Loan Amount ($)
                    </label>
                    <Input
                      type="number"
                      placeholder="485000"
                      value={inputs.averageLoanAmount}
                      onChange={(e) => handleInputChange('averageLoanAmount', e.target.value)}
                      className="text-lg"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Working Days per Month
                    </label>
                    <Input
                      type="number"
                      placeholder="20"
                      value={inputs.workingDaysPerMonth}
                      onChange={(e) => handleInputChange('workingDaysPerMonth', e.target.value)}
                      className="text-lg"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your Commission Split (%)
                    </label>
                    <Input
                      type="number"
                      placeholder="50"
                      min="0"
                      max="100"
                      value={inputs.brokerSplit}
                      onChange={(e) => handleInputChange('brokerSplit', e.target.value)}
                      className="text-lg"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Your percentage of the 0.66% upfront commission
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Conversion Rates */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                    Conversion Rates (%)
                  </CardTitle>
                  <CardDescription>
                    Set your expected conversion rates between each stage
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Lead → Discovery
                      </label>
                      <Input
                        type="number"
                        placeholder="70"
                        value={inputs.leadToDiscovery}
                        onChange={(e) => handleInputChange('leadToDiscovery', e.target.value)}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Discovery → Portal
                      </label>
                      <Input
                        type="number"
                        placeholder="70"
                        value={inputs.discoveryToPortal}
                        onChange={(e) => handleInputChange('discoveryToPortal', e.target.value)}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Portal → Fact Find
                      </label>
                      <Input
                        type="number"
                        placeholder="70"
                        value={inputs.portalToFactFind}
                        onChange={(e) => handleInputChange('portalToFactFind', e.target.value)}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Fact Find → Assessment
                      </label>
                      <Input
                        type="number"
                        placeholder="70"
                        value={inputs.factFindToAssessment}
                        onChange={(e) => handleInputChange('factFindToAssessment', e.target.value)}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Assessment → Proposal
                      </label>
                      <Input
                        type="number"
                        placeholder="70"
                        value={inputs.assessmentToProposal}
                        onChange={(e) => handleInputChange('assessmentToProposal', e.target.value)}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Proposal → Application
                      </label>
                      <Input
                        type="number"
                        placeholder="70"
                        value={inputs.proposalToApplication}
                        onChange={(e) => handleInputChange('proposalToApplication', e.target.value)}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Application → Lodgement
                      </label>
                      <Input
                        type="number"
                        placeholder="70"
                        value={inputs.applicationToLodgement}
                        onChange={(e) => handleInputChange('applicationToLodgement', e.target.value)}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Lodgement → Approval
                      </label>
                      <Input
                        type="number"
                        placeholder="70"
                        value={inputs.lodgementToApproval}
                        onChange={(e) => handleInputChange('lodgementToApproval', e.target.value)}
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Approval → Settlement
                      </label>
                      <Input
                        type="number"
                        placeholder="70"
                        value={inputs.approvalToSettlement}
                        onChange={(e) => handleInputChange('approvalToSettlement', e.target.value)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            /* Results Display */
            <div className="space-y-8">
              {/* Commission Summary */}
              <Card className="border-2 border-green-200 bg-green-50">
                <CardHeader className="text-center">
                  <CardTitle className="flex items-center justify-center gap-3 text-2xl text-green-800">
                    <DollarSign className="h-8 w-8" />
                    Commission Breakdown
                  </CardTitle>
                  <CardDescription className="text-lg">
                    Based on {formatCurrency(results.settlementGoal)} settlement goal
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-4 text-center">
                    <div className="bg-white p-6 rounded-lg border shadow">
                      <div className="text-sm text-gray-600 mb-2">Gross Commission (0.66%)</div>
                      <div className="text-3xl font-bold text-green-600">
                        {formatCurrency(results.commission.gross)}
                      </div>
                    </div>
                    <div className="bg-white p-6 rounded-lg border shadow">
                      <div className="text-sm text-gray-600 mb-2">Your Commission ({results.brokerSplit}%)</div>
                      <div className="text-3xl font-bold text-blue-600">
                        {formatCurrency(results.commission.broker)}
                      </div>
                    </div>
                    <div className="bg-white p-6 rounded-lg border shadow">
                      <div className="text-sm text-gray-600 mb-2">Company Commission</div>
                      <div className="text-3xl font-bold text-purple-600">
                        {formatCurrency(results.commission.company)}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Required Activity Funnel */}
              <Card className="border-2 border-purple-200 bg-purple-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-purple-800">
                    <BarChart3 className="h-5 w-5" />
                    Required Activity Levels
                  </CardTitle>
                  <CardDescription>
                    Monthly, weekly, and daily targets for each stage
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {funnelStages.map((stage) => {
                      const StageIcon = stage.icon;
                      const stageData = results.funnel[stage.key];
                      
                      return (
                        <div key={stage.key} className={`p-4 rounded-lg border-2 ${stage.color}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <StageIcon className="h-5 w-5" />
                              <h4 className="font-semibold">{stage.label}</h4>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-4 text-center">
                            <div>
                              <div className="text-xs text-gray-600">Monthly</div>
                              <div className="text-lg font-bold">{formatNumber(stageData.monthly)}</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-600">Weekly</div>
                              <div className="text-lg font-bold">{formatNumber(stageData.weekly)}</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-600">Daily</div>
                              <div className="text-lg font-bold">{formatNumber(stageData.daily)}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Goals Summary */}
              <Card className="border-2 border-blue-200 bg-blue-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-800">
                    <Target className="h-5 w-5" />
                    Your Blueprint Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Target Overview</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Settlement Goal:</span>
                          <span className="font-semibold">{formatCurrency(results.settlementGoal)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Required Settlements:</span>
                          <span className="font-semibold">{formatNumber(results.funnel.settlements.monthly)} loans</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Average Loan Amount:</span>
                          <span className="font-semibold">{formatCurrency(results.averageLoanAmount)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Working Days:</span>
                          <span className="font-semibold">{results.workingDays} days/month</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Key Daily Targets</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Leads Required:</span>
                          <span className="font-semibold text-red-600">{formatNumber(results.funnel.leads.daily)}/day</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Discovery Calls:</span>
                          <span className="font-semibold text-orange-600">{formatNumber(results.funnel.discovery.daily)}/day</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Applications:</span>
                          <span className="font-semibold text-blue-600">{formatNumber(results.funnel.applications.daily)}/day</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Settlements:</span>
                          <span className="font-semibold text-purple-600">{formatNumber(results.funnel.settlements.daily)}/day</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            {!showResults ? (
              <Button 
                onClick={calculateBlueprint}
                size="lg"
                className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 text-lg"
                disabled={!inputs.settlementGoal || !inputs.averageLoanAmount}
              >
                <Calculator className="mr-2 h-5 w-5" />
                Calculate My Blueprint
              </Button>
            ) : (
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={resetCalculator}
                  size="lg"
                  variant="outline"
                  className="px-8 py-3 text-lg"
                >
                  Calculate Another Blueprint
                </Button>
                <Button 
                  onClick={exportToPDF}
                  size="lg"
                  className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-3 text-lg"
                >
                  <FileText className="mr-2 h-5 w-5" />
                  Print PDF Report
                </Button>
                <Button 
                  onClick={exportToCSV}
                  size="lg"
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 text-lg"
                >
                  <Download className="mr-2 h-5 w-5" />
                  Export CSV Data
                </Button>
              </div>
            )}
          </div>

          {/* Disclaimer */}
          <Card className="mt-8 border-orange-200 bg-orange-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <div className="h-6 w-6 text-orange-600 mt-0.5 flex-shrink-0">⚠️</div>
                <div>
                  <h4 className="font-semibold text-orange-800 mb-2">Important Disclaimer</h4>
                  <p className="text-sm text-orange-700 leading-relaxed">
                    This Blueprint Break Down calculator provides estimates for planning purposes only and should not be considered as guaranteed outcomes. 
                    Actual conversion rates, market conditions, and settlement volumes may vary significantly. Commission calculations are based on the 
                    standard 0.66% upfront rate and your specified split, but actual commissions may depend on specific lender agreements and loan products. 
                    We recommend using this tool as a guide alongside your professional experience and market knowledge. Always consult with management 
                    regarding commission structures and business planning strategies.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BlueprintBreakDown;